// src/components/DiagramViewer/DiagramViewer.jsx
import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './DiagramViewer.css';

const CONTRAST_FILTERS = {
  normal: 'none',
  high: 'contrast(1.8) brightness(1.1)',
  inverted: 'invert(1) contrast(1.4)',
};

const DiagramViewer = memo(function DiagramViewer({
  image, components, selectedId, zoom, setZoom, pan, setPan,
  diagramContrast, onSelectComponent, autoZoom,
}) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [pulseId, setPulseId] = useState(null);

  useEffect(() => {
    const canvas = overlayRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;
    const rect = img.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    components.forEach((comp) => {
      const x = (comp.bbox.x / 100) * canvas.width;
      const y = (comp.bbox.y / 100) * canvas.height;
      const w = (comp.bbox.w / 100) * canvas.width;
      const h = (comp.bbox.h / 100) * canvas.height;
      const isSelected = comp.id === selectedId;
      if (isSelected) {
        ctx.save();
        ctx.shadowColor = comp.color;
        ctx.shadowBlur = 28;
        ctx.strokeStyle = comp.color;
        ctx.lineWidth = 2.5;
        ctx.strokeRect(x, y, w, h);
        ctx.fillStyle = comp.color + '22';
        ctx.fillRect(x, y, w, h);
        ctx.shadowBlur = 0;
        ctx.font = 'bold 10px "Space Mono", monospace';
        const label = comp.ref + ' · ' + comp.name;
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = 'rgba(9,14,26,0.9)';
        ctx.fillRect(x, y - 20, tw + 12, 17);
        ctx.fillStyle = comp.color;
        ctx.fillText(label, x + 6, y - 7);
        ctx.restore();
      } else {
        ctx.save();
        ctx.strokeStyle = 'rgba(56,100,200,0.3)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.restore();
      }
    });
  }, [components, selectedId, imgLoaded, zoom]);

  useEffect(() => {
    if (!autoZoom || !selectedId || !containerRef.current) return;
    const comp = components.find((c) => c.id === selectedId);
    if (!comp) return;
    const container = containerRef.current;
    const targetZoom = 2.5;
    const cxPct = comp.bbox.x + comp.bbox.w / 2;
    const cyPct = comp.bbox.y + comp.bbox.h / 2;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    setZoom(targetZoom);
    setPan({
      x: cw / 2 - (cxPct / 100) * cw * targetZoom,
      y: ch / 2 - (cyPct / 100) * ch * targetZoom,
    });
    setPulseId(selectedId);
    setTimeout(() => setPulseId(null), 1000);
  }, [selectedId]); // eslint-disable-line

  const onMouseDown = useCallback((e) => {
    isDragging.current = true;
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  }, [pan]);
  const onMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    setPan({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  }, [setPan]);
  const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.12 : 0.12;
      setZoom((z) => Math.min(Math.max(z + delta, 0.25), 4));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [setZoom]);

  const handleCanvasClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = ((e.clientX - rect.left) / rect.width) * 100;
    const cy = ((e.clientY - rect.top) / rect.height) * 100;
    const hit = components.find((c) =>
      cx >= c.bbox.x && cx <= c.bbox.x + c.bbox.w &&
      cy >= c.bbox.y && cy <= c.bbox.y + c.bbox.h
    );
    onSelectComponent(hit ? hit.id : null);
  }, [components, onSelectComponent]);

  if (!image) {
    return (
      <div className="viewer-empty">
        <div className="empty-grid" />
        <div className="empty-content">
          <div className="empty-hex">⬡</div>
          <p className="empty-title">No Diagram Loaded</p>
          <p className="empty-sub">Upload a circuit image to begin component analysis</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="viewer-container"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <motion.div
        className="viewer-inner"
        animate={{ scale: zoom, x: pan.x, y: pan.y }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        style={{ transformOrigin: '0 0', cursor: 'grab' }}
      >
        <div className="img-wrapper">
          <img
            ref={imgRef}
            src={image.url}
            alt="Circuit diagram"
            className="diagram-img"
            style={{ filter: CONTRAST_FILTERS[diagramContrast] }}
            onLoad={() => setImgLoaded(true)}
            draggable={false}
          />
          <canvas ref={overlayRef} className="overlay-canvas" onClick={handleCanvasClick} />
          <AnimatePresence>
            {pulseId && (() => {
              const comp = components.find((c) => c.id === pulseId);
              if (!comp) return null;
              return (
                <motion.div
                  key={'pulse-' + pulseId}
                  className="pulse-ring"
                  initial={{ scale: 1, opacity: 0.9 }}
                  animate={{ scale: 3, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  style={{
                    left: (comp.bbox.x + comp.bbox.w / 2) + '%',
                    top: (comp.bbox.y + comp.bbox.h / 2) + '%',
                    borderColor: comp.color,
                    boxShadow: '0 0 12px ' + comp.color,
                  }}
                />
              );
            })()}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
});

export default DiagramViewer;
